import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';

import {
  BlendingModeIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CubeIcon,
  ExitIcon,
  FileTextIcon,
  HomeIcon,
  // CopyIcon, // Temporarily disabled with Look Like feature
  ImageIcon,
  LayoutIcon,
  LightningBoltIcon,
  MixerHorizontalIcon,
  PersonIcon,
  PlusIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { ROUTES } from '@/shared/config/routes';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { AnimatedCredits } from '@/shared/ui/animated-credits';

interface MenuItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

// Define NavItem component outside to prevent recreation on each render
const NavItem = memo(
  ({
    item,
    isActive,
    isCollapsed,
  }: {
    item: MenuItem;
    isActive: boolean;
    isCollapsed: boolean;
  }) => {
    return (
      <div>
        <Link
          to={item.href}
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
            'hover:bg-purple-500/10',
            isActive && 'bg-gradient-to-r from-violet-500 to-purple-400 text-white shadow-lg shadow-purple-500/25',
            !isActive && 'text-gray-600 hover:text-purple-600',
            isCollapsed && 'justify-center',
          )}
        >
          <motion.span
            className={clsx("flex-shrink-0", isActive ? "text-white" : "text-purple-600")}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {item.icon}
          </motion.span>
          <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                className="text-sm font-medium whitespace-nowrap"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
        </Link>
      </div>
    );
  },
);

NavItem.displayName = 'NavItem';

// Category Header Component with collapsible functionality
const CategoryHeader = memo(
  ({
    label,
    isExpanded,
    onToggle,
    isCollapsed,
  }: {
    label: string;
    isExpanded: boolean;
    onToggle: () => void;
    isCollapsed: boolean;
  }) => {
    return (
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-500 hover:text-purple-600 hover:bg-purple-500/10 transition-all duration-200"
      >
        <span className="text-sm font-medium">{!isCollapsed && label}</span>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 text-purple-500"
        >
          <ChevronDownIcon className="w-4 h-4" />
        </motion.div>
      </button>
    );
  },
);

CategoryHeader.displayName = 'CategoryHeader';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get sidebar state from store
  const { isCollapsed, toggle } = useSidebarStore();

  // State for category collapse/expand (persisted in localStorage)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sidebarExpandedCategories');
    return saved ? JSON.parse(saved) : {
      models: true,
      createContent: true,
      animate: true,
      aiPosting: false
    };
  });

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarExpandedCategories', JSON.stringify(expandedCategories));
  }, [expandedCategories]);

  // Toggle category expansion
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Optimize Zustand subscriptions with shallow equality check
  const { user, isAuthenticated, isInitializing, isCreditsLoading } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isInitializing: state.isInitializing,
      isCreditsLoading: state.isCreditsLoading,
    })),
  );

  // Get logout function separately to avoid re-renders
  const logout = useAuthStore((state) => state.logout);

  // Memoize event handlers to maintain referential equality
  const handleNavigateToProfile = useCallback(() => {
    navigate(ROUTES.PROFILE);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate(ROUTES.HOME);
    // Removed window.location.reload() to avoid potential infinite loops
  }, [logout, navigate]);

  const handleCreditsClick = useCallback(() => {
    navigate(ROUTES.SUBSCRIPTIONS);
  }, [navigate]);

  // Check if user is a mayatech admin
  const isMayatechAdmin = useMemo(() => {
    return user?.isAdmin && user?.email?.endsWith('@mayatech.ai');
  }, [user]);

  // Organize menu items by categories
  const modelsMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Create Your AI',
        href: ROUTES.CREATE,
        icon: <PlusIcon className="w-5 h-5" />,
      },
      {
        label: 'My Models',
        href: ROUTES.MODEL,
        icon: <CubeIcon className="w-5 h-5" />,
      },
    ],
    [],
  );

  const createContentMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Create Images',
        href: ROUTES.IMAGE_STUDIO,
        icon: <ImageIcon className="w-5 h-5" />,
      },

      // Create Rooms (Stage Designer) — admin only
      ...(isMayatechAdmin
        ? [
            {
              label: 'Create Rooms',
              href: ROUTES.STAGE_DESIGNER,
              icon: <LayoutIcon className="w-5 h-5" />,
            },
          ]
        : []),
      {
        label: 'Edit Images',
        href: ROUTES.IMAGE_EDITOR,
        icon: <MixerHorizontalIcon className="w-5 h-5" />,
      },
      {
        label: 'Prompt Library',
        href: ROUTES.PROMPT_LIBRARY,
        icon: <FileTextIcon className="w-5 h-5" />,
      },
    ],
    [isMayatechAdmin],
  );

  const animateMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Create Reels',
        href: ROUTES.VIDEO_STUDIO,
        icon: <VideoIcon className="w-5 h-5" />,
      },

    ],
    [],
  );

  const aiPostingMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Create Accounts',
        href: '#', // Placeholder - no page yet
        icon: <PersonIcon className="w-5 h-5" />,
      },
      {
        label: 'Connect Accounts',
        href: '#', // Placeholder - no page yet
        icon: <BlendingModeIcon className="w-5 h-5" />,
      },
      {
        label: 'Posts Planning',
        href: '#', // Placeholder - no page yet
        icon: <FileTextIcon className="w-5 h-5" />,
      },
    ],
    [],
  );

  // Memoize home menu item
  const homeMenuItem = useMemo(
    () => ({
      label: 'Home',
      href: ROUTES.HOME,
      icon: <HomeIcon className="w-5 h-5" />,
    }),
    [],
  );

  return (
    <motion.aside
      animate={{
        width: isCollapsed ? 64 : 256,
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 40,
        mass: 1,
      }}
      className="sticky top-0 h-screen bg-white border-r border-gray-100 flex flex-col shadow-lg"
    >
      {/* Logo/Brand */}
      <div className="border-b border-purple-500/10 relative">
        <div
          className={clsx(
            'flex items-center',
            isCollapsed ? 'justify-center flex-col gap-2' : 'justify-between',
          )}
        >
          {isCollapsed ? (
            <div className="py-2 flex flex-col gap-2 items-center">
              <div>
                <Button
                  variant="light"
                  size="sm"
                  className="w-full justify-center text-gray-600 hover:text-purple-600 hover:bg-purple-500/10 transition-all duration-300 rounded-lg cursor-pointer"
                  onPress={toggle}
                  data-sidebar-toggle
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </Button>
              </div>
              <Link to={ROUTES.HOME}>
                <motion.div
                  className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-400 rounded-lg flex-shrink-0 flex items-center justify-center cursor-pointer shadow-lg shadow-purple-500/30"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LightningBoltIcon className="w-5 h-5 text-white" />
                </motion.div>
              </Link>
            </div>
          ) : (
            <div className="flex justify-around px-3 items-center h-[64px]">
              <Link to={ROUTES.HOME} className="flex items-center gap-2 cursor-pointer">
                <motion.div
                  className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-400 rounded-lg flex-shrink-0 flex items-center justify-center shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LightningBoltIcon className="w-5 h-5 text-white" />
                </motion.div>
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.h1
                      className="text-xl font-bold text-gray-900"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                      FameCreator
                    </motion.h1>
                  )}
                </AnimatePresence>
              </Link>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="ml-2">
                <Button
                  variant="light"
                  size="sm"
                  className="flex-shrink-0 text-gray-600 hover:text-purple-600 hover:bg-purple-500/10 transition-all duration-300 rounded-lg cursor-pointer"
                  onPress={toggle}
                  data-sidebar-toggle
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </Button>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-3 overflow-y-auto">
        {/* Home item */}
        <div>
          <NavItem
            key={ROUTES.HOME}
            item={homeMenuItem}
            isActive={location.pathname === homeMenuItem.href}
            isCollapsed={isCollapsed}
          />
        </div>

        {/* Models Category */}
        <div>
          <CategoryHeader
            label="Models"
            isExpanded={expandedCategories.models}
            onToggle={() => toggleCategory('models')}
            isCollapsed={isCollapsed}
          />
          <AnimatePresence>
            {expandedCategories.models && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 mt-1 pl-2">
                  {modelsMenuItems.map((item) => (
                    <NavItem
                      key={item.href}
                      item={item}
                      isActive={location.pathname === item.href}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Create Content Category */}
        <div>
          <CategoryHeader
            label="Create Content"
            isExpanded={expandedCategories.createContent}
            onToggle={() => toggleCategory('createContent')}
            isCollapsed={isCollapsed}
          />
          <AnimatePresence>
            {expandedCategories.createContent && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 mt-1 pl-2">
                  {createContentMenuItems.map((item) => {
                    // Special case: Stage Designer menu should be active for both /stage-designer and /staging
                    const isActive =
                      location.pathname === item.href ||
                      (item.href === ROUTES.STAGE_DESIGNER && location.pathname === '/staging');

                    return (
                      <NavItem
                        key={item.href}
                        item={item}
                        isActive={isActive}
                        isCollapsed={isCollapsed}
                      />
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Animate Category */}
        <div>
          <CategoryHeader
            label="Animate"
            isExpanded={expandedCategories.animate}
            onToggle={() => toggleCategory('animate')}
            isCollapsed={isCollapsed}
          />
          <AnimatePresence>
            {expandedCategories.animate && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 mt-1 pl-2">
                  {animateMenuItems.map((item) => (
                    <NavItem
                      key={item.href}
                      item={item}
                      isActive={location.pathname === item.href}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* AI Posting Category - Only visible to mayatech admins */}
        {isMayatechAdmin && (
          <div>
            <CategoryHeader
              label="AI Posting"
              isExpanded={expandedCategories.aiPosting}
              onToggle={() => toggleCategory('aiPosting')}
              isCollapsed={isCollapsed}
            />
            <AnimatePresence>
              {expandedCategories.aiPosting && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-1 mt-1 pl-2">
                    {aiPostingMenuItems.map((item) => (
                      <NavItem
                        key={item.href}
                        item={item}
                        isActive={location.pathname === item.href}
                        isCollapsed={isCollapsed}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </nav>

      {/* User Profile Section at Bottom */}
      <div className="border-t border-purple-500/10 p-4">
        {/* Subscribe Button for users without subscription */}
        {!isInitializing && isAuthenticated && !user?.hasActiveSubscription && !isCollapsed && (
          <div className="mb-3">
            <Button
              as={Link}
              to={ROUTES.SUBSCRIPTIONS}
              className="w-full bg-gradient-to-r from-violet-500 to-purple-400 hover:shadow-lg hover:shadow-purple-500/30 text-white rounded-lg cursor-pointer transition-all duration-300"
              size="sm"
            >
              Subscribe
            </Button>
          </div>
        )}

        {/* User Profile Dropdown / Login Button */}
        {isInitializing ? (
          // Show loading state while initializing
          <div className="w-full flex items-center justify-center py-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
          </div>
        ) : isAuthenticated ? (
          <Dropdown placement="top" className="w-full">
            <DropdownTrigger>
              <button
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/50 hover:bg-purple-500/10 border border-purple-500/10 transition-all duration-200 cursor-pointer shadow-sm',
                  isCollapsed && 'justify-center',
                )}
              >
                <PersonIcon className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      className="text-sm font-medium text-gray-800 truncate min-w-0"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      title={user?.email || 'Profile'}
                    >
                      {user?.email || 'Profile'}
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="User menu"
              variant="faded"
              className="bg-white/90 backdrop-blur-xl border-purple-500/20 rounded-lg shadow-xl"
              classNames={{
                base: 'min-w-[200px] max-w-[240px] w-auto rounded-lg',
              }}
            >
              {/* Credits Display in Dropdown */}
              <DropdownItem
                key="credits"
                className="text-gray-800 hover:bg-purple-500/10 cursor-pointer p-0"
                onPress={handleCreditsClick}
                textValue="Credits"
              >
                <div className="flex items-center gap-2 text-gray-800 px-2 py-1.5 w-full">
                  <span className="text-yellow-500 font-bold text-base">💰</span>
                  {isCreditsLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                  ) : (
                    <AnimatedCredits credits={user?.credits || 0} />
                  )}
                </div>
              </DropdownItem>
              <DropdownItem
                key="profile"
                startContent={<PersonIcon className="w-4 h-4 text-purple-600" />}
                className="text-gray-800 hover:bg-purple-500/10 cursor-pointer"
                onPress={handleNavigateToProfile}
                textValue="Profile"
              >
                Profile
              </DropdownItem>
              <DropdownItem
                key="logout"
                startContent={<ExitIcon className="w-4 h-4 text-red-500" />}
                onPress={handleLogout}
                className="text-red-500 hover:bg-red-500/10 cursor-pointer"
                color="danger"
                textValue="Log out"
              >
                Log out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        ) : (
          <Button
            as={Link}
            to="/auth/sign-in"
            className={clsx(
              'w-full bg-gradient-to-r from-violet-500 to-purple-400 hover:shadow-lg hover:shadow-purple-500/30 text-white rounded-xl cursor-pointer flex items-center justify-center transition-all duration-300',
              isCollapsed && 'px-2',
            )}
            size="sm"
          >
            {isCollapsed ? <PersonIcon className="w-4 h-4" /> : 'Sign In'}
          </Button>
        )}
      </div>
    </motion.aside>
  );
};
